import { useId } from 'react'
import clsx from 'clsx'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

type LevelIndicatorBarProps = {
    completedStages: number
    className?: string
    showChameleIndicator?: boolean
}

const BAR_PATH = 'M10.0307 26.3185C70.7339 11.144 134.225 11.0912 194.954 26.1647'
const SHINE_PATH = 'M9.74552 24.4796C70.7656 9.22593 134.589 9.17287 195.634 24.3251'
const STAGE_POSITIONS = [10, 54, 103, 152, 195] as const

function clampStages(completedStages: number) {
    return Math.min(Math.max(Math.round(completedStages), 0), 4)
}

export default function LevelIndicatorBar({
    completedStages,
    className,
    showChameleIndicator = true,
}: LevelIndicatorBarProps) {
    const isStele = useSteleBreakpoint()
    const baseId = useId().replace(/:/g, '')
    const progressClipId = `${baseId}-progress-clip`
    const shadowGradientId = `${baseId}-shadow-gradient`
    const fillGradientId = `${baseId}-fill-gradient`
    const markerGradientId = `${baseId}-marker-gradient`
    const shineGradientId = `${baseId}-shine-gradient`
    const filterId = `${baseId}-shadow-filter`
    const clampedStages = clampStages(completedStages)
    const progressWidth = STAGE_POSITIONS[clampedStages]
    const indicatorLeft = `${(progressWidth / 206) * 100}%`
    const markerStates = [1, 2, 3].map((stage) => clampedStages >= stage)

    return (
        <div className={clsx('relative', className)}>
            <svg
                width={isStele ? 720 : 206}
                height={isStele ? 136 : 39}
                viewBox='0 0 206 39'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='w-full h-auto'
                role='meter'
                aria-label='Level indicator progress'
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={clampedStages}
            >
                <path d={BAR_PATH} stroke='#5A000F' strokeWidth='14' strokeLinecap='round' strokeLinejoin='round' />
                <g filter={`url(#${filterId})`}>
                    <path d={BAR_PATH} stroke={`url(#${shadowGradientId})`} strokeWidth='20' strokeLinecap='round' strokeLinejoin='round' />
                </g>
                <path d={BAR_PATH} stroke='#5A000F' strokeWidth='14' strokeLinecap='round' strokeLinejoin='round' />

                <g clipPath={`url(#${progressClipId})`}>
                    <path d={BAR_PATH} stroke={`url(#${fillGradientId})`} strokeWidth='14' strokeLinecap='round' strokeLinejoin='round' />
                    <path d={BAR_PATH} opacity='0.35' stroke={`url(#${fillGradientId})`} strokeWidth='10' strokeLinecap='round' strokeLinejoin='round' />
                </g>

                <circle cx='54' cy='18' r='2' fill={markerStates[0] ? `url(#${markerGradientId})` : '#8E5A63'} />
                <circle cx='103' cy='15' r='2' fill={markerStates[1] ? `url(#${markerGradientId})` : '#8E5A63'} />
                <circle cx='152' cy='18' r='2' fill={markerStates[2] ? `url(#${markerGradientId})` : '#8E5A63'} />

                <path d={SHINE_PATH} stroke={`url(#${shineGradientId})`} strokeWidth='4' strokeLinecap='round' strokeLinejoin='round' />

                <defs>
                    <clipPath id={progressClipId}>
                        <rect x='0' y='0' width={progressWidth} height='39' style={{ transition: 'width 180ms ease-out' }} />
                    </clipPath>
                    <filter id={filterId} x='0.0292969' y='4.89844' width='204.926' height='33.4224' filterUnits='userSpaceOnUse' colorInterpolationFilters='sRGB'>
                        <feFlood floodOpacity='0' result='BackgroundImageFix' />
                        <feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha' />
                        <feOffset dy='2' />
                        <feComposite in2='hardAlpha' operator='out' />
                        <feColorMatrix type='matrix' values='0 0 0 0 0.330912 0 0 0 0 0.523656 0 0 0 0 0.676245 0 0 0 1 0' />
                        <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow' />
                        <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape' />
                    </filter>
                    <linearGradient id={shadowGradientId} x1='102.802' y1='781.898' x2='102.802' y2='14.8984' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#C8E3F7' />
                        <stop offset='1' stopColor='white' />
                    </linearGradient>
                    <radialGradient id={fillGradientId} cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(102.801 398.398) rotate(-90) scale(388.898 388.391)'>
                        <stop offset='0.973426' stopColor='#E96178' />
                        <stop offset='1' stopColor='#DD0B2F' />
                    </radialGradient>
                    <linearGradient id={markerGradientId} x1='103' y1='17' x2='103' y2='13' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#C8E3F7' />
                        <stop offset='1' stopColor='white' />
                    </linearGradient>
                    <radialGradient id={shineGradientId} cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(103.66 784) rotate(-90) scale(778.261 576.128)'>
                        <stop offset='0.989297' stopColor='white' stopOpacity='0' />
                        <stop offset='0.996184' stopColor='white' />
                    </radialGradient>
                </defs>
            </svg>

            {showChameleIndicator && (
                <div
                    className={clsx(
                        'top-1/2 z-10 absolute transition-[left] -translate-x-1/2 -translate-y-[50%] duration-180 ease-out',
                    )}
                    style={{ left: indicatorLeft }}
                >
                    <img
                        src='/images/ChameleRun.gif'
                        className={clsx(
                            'w-8 h-8',
                            'min-w-8 min-h-8',
                            'stele:w-32 stele:h-32',
                            'stele:min-w-32 stele:min-h-32'
                        )}
                    />
                </div>
            )}
        </div>
    )
}
